import { Colors } from '@src/colors';
import React, { FunctionComponent, useEffect, useState } from 'react';
import './validated-input.scss';
import { WireIcon } from './wire-icon';

export const ValidatedInput: FunctionComponent<{
    dataCy?: string;
    placeholder?: string;
    onChange: (valid: boolean, value?: string) => void;
    value?: string;
    /** Regular Expression pattern */
    pattern: string;
    required?: boolean;
    autoFocus?: boolean;
    invalid?: boolean;
    invalidMessage?: string;
    ariaLabel?: string;
    disabled?: boolean;
    onEnterKeyPressed?: () => void;
}> = ({
    dataCy,
    placeholder,
    onChange,
    pattern,
    required = true,
    autoFocus,
    value: initialValue,
    invalid = false,
    invalidMessage = '',
    ariaLabel = placeholder,
    disabled = false,
    onEnterKeyPressed,
}) => {
    const [value, setValue] = useState(initialValue);
    const [validated, setValidated] = useState(false);
    const [valid, setValid] = useState(false);
    const re = new RegExp(pattern);

    useEffect(() => {
        setValid(value !== undefined ? re.test(value) : !required);
    }, []);

    useEffect(() => onChange(valid, value), [value, valid]);
    useEffect(() => setValid(!invalid), [invalid]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value !== '' ? e.target.value : undefined;
        const okay = value !== undefined ? re.test(value) : !required;

        setValid(okay);
        setValue(value);

        e.target.setCustomValidity(okay ? '' : invalidMessage);

        // don't show validation icon for empty fields that aren't required
        setValidated(value !== undefined || required);
    };

    return (
        <div className="validated-input">
            <input
                data-cy={dataCy}
                placeholder={placeholder}
                aria-label={ariaLabel}
                onChange={handleChange}
                defaultValue={value}
                onKeyDown={(e) => {
                    if (onEnterKeyPressed && e.key?.trim()?.toLowerCase() === 'enter') {
                        onEnterKeyPressed();
                    }
                }}
                autoFocus={autoFocus}
                disabled={disabled}
                type="text"
            ></input>
            {validated &&
                (valid ? (
                    <WireIcon className="valid" colorName={Colors.moss} svg="check-circle" />
                ) : (
                    <>
                        <WireIcon className="invalid" colorName={Colors.sunset} svg="x-circle" />
                        <div className="error">{invalidMessage}</div>
                    </>
                ))}
        </div>
    );
};
